import { useState, useEffect } from "react";
import { Role, PermissionLevel } from "@/types/rbac";
import { useCreateRole, useUpdateRole, useResources } from "@/hooks/rbac";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight, User, Shield, CheckCircle, Loader2 } from "lucide-react";
import RolePermissionSelector from "./RolePermissionSelector";

interface RoleWizardProps {
  role: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WizardStep = 'details' | 'permissions' | 'review';

export default function RoleWizard({ role, open, onOpenChange }: RoleWizardProps) {
  const { mutate: createRole, isPending: isCreating } = useCreateRole();
  const { mutate: updateRole, isPending: isUpdating } = useUpdateRole();
  const { data: resources } = useResources();
  
  const [currentStep, setCurrentStep] = useState<WizardStep>('details');
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [permissions, setPermissions] = useState<Record<string, PermissionLevel>>({});
  
  const isSystemRole = role?.is_system;
  const isEditMode = !!role;
  const isPending = isCreating || isUpdating;

  // Reset form when dialog opens/closes or role changes
  useEffect(() => {
    if (open) {
      if (role) {
        setRoleName(role.name);
        setRoleDescription(role.description || "");
      } else {
        setRoleName("");
        setRoleDescription("");
        setPermissions({});
        setCurrentStep('details');
      }
    }
  }, [open, role]);

  // Initialize permissions for edit mode
  useEffect(() => {
    if (!isEditMode && resources) {
      const initialPermissions: Record<string, PermissionLevel> = {};
      resources.forEach(resource => {
        initialPermissions[resource.id] = 'none';
      });
      setPermissions(initialPermissions);
    }
  }, [resources, isEditMode]);

  const canProceed = () => {
    if (currentStep === 'details') {
      return roleName.trim().length > 0;
    }
    return true;
  };

  const getPermissionCount = () => {
    return Object.values(permissions).filter(p => p !== 'none').length;
  };

  const handleNext = () => {
    if (currentStep === 'details') {
      setCurrentStep('permissions');
    } else if (currentStep === 'permissions') {
      setCurrentStep('review');
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'permissions') {
      setCurrentStep('details');
    } else if (currentStep === 'review') {
      setCurrentStep('permissions');
    }
  };

  const handleSave = () => {
    if (isEditMode) {
      updateRole({
        id: role.id,
        name: roleName,
        description: roleDescription
      });
    } else {
      const permissionsArray = resources
        ?.map(resource => ({
          resource_id: resource.id,
          permission: permissions[resource.id] || 'none'
        }))
        .filter(p => p.permission !== 'none') || [];

      createRole({
        name: roleName,
        description: roleDescription,
        permissions: permissionsArray
      });
    }
    onOpenChange(false);
  };

  const getStepIcon = (step: WizardStep) => {
    const icons = {
      details: User,
      permissions: Shield,
      review: CheckCircle
    };
    const Icon = icons[step];
    return <Icon className="h-4 w-4" />;
  };

  const getStepTitle = () => {
    const titles = {
      details: 'Role Details',
      permissions: 'Set Permissions',
      review: 'Review & Create'
    };
    return titles[currentStep];
  };

  const renderStepIndicator = () => {
    const steps: WizardStep[] = ['details', 'permissions', 'review'];
    
    return (
      <div className="flex items-center justify-center space-x-2 mb-6">
        {steps.map((step, index) => {
          const isActive = step === currentStep;
          const isCompleted = steps.indexOf(currentStep) > index;
          
          return (
            <div key={step} className="flex items-center">
              <div className={`
                flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors
                ${isActive ? 'border-primary bg-primary text-primary-foreground' : 
                  isCompleted ? 'border-green-500 bg-green-500 text-white' : 
                  'border-muted-foreground bg-background'}
              `}>
                {getStepIcon(step)}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-2 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailsStep = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Role Name *</Label>
        <Input
          id="name"
          placeholder="Enter role name"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          disabled={isSystemRole}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Enter role description"
          value={roleDescription}
          onChange={(e) => setRoleDescription(e.target.value)}
          className="min-h-[80px]"
          disabled={isSystemRole}
        />
      </div>
    </div>
  );

  const renderPermissionsStep = () => (
    <RolePermissionSelector
      permissions={permissions}
      onPermissionsChange={setPermissions}
    />
  );

  const renderReviewStep = () => (
    <div className="space-y-4">
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle>Role Summary</GlassCardTitle>
          <GlassCardDescription>Review the role details before creating</GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Role Name</Label>
            <p className="text-sm text-muted-foreground mt-1">{roleName}</p>
          </div>
          
          {roleDescription && (
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <p className="text-sm text-muted-foreground mt-1">{roleDescription}</p>
            </div>
          )}

          <Separator />

          <div>
            <Label className="text-sm font-medium">Permissions</Label>
            <div className="mt-2">
              <Badge variant="secondary" className="mb-2">
                {getPermissionCount()} resources configured
              </Badge>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {resources
                  ?.filter(resource => permissions[resource.id] !== 'none')
                  .map(resource => (
                    <div key={resource.id} className="flex justify-between text-sm">
                      <span>{resource.name}</span>
                      <Badge variant="outline">
                        {permissions[resource.id]}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'details':
        return renderDetailsStep();
      case 'permissions':
        return renderPermissionsStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? `Edit ${role.name}` : 'Create New Role'}
          </DialogTitle>
        </DialogHeader>

        {!isEditMode && renderStepIndicator()}

        <div className="space-y-4">
          {!isEditMode && (
            <div className="text-center">
              <h3 className="text-lg font-semibold">{getStepTitle()}</h3>
            </div>
          )}

          {renderCurrentStep()}
        </div>

        <div className="flex justify-between pt-4">
          <div>
            {!isEditMode && currentStep !== 'details' && (
              <Button variant="outline" onClick={handlePrevious}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            
            {!isEditMode && currentStep !== 'review' ? (
              <Button onClick={handleNext} disabled={!canProceed()}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button 
                onClick={handleSave}
                disabled={isPending || !canProceed() || isSystemRole}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Update Role' : 'Create Role'
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}